import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  InputAdornment,
  Divider,
  Chip,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import MapIcon from "@mui/icons-material/Map";
import MarkunreadMailboxIcon from "@mui/icons-material/MarkunreadMailbox";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Services
import { authAPI } from "../../services/api";

// Utils and Constants
import { isValidEmail } from "../../utils/validation";
import { INDUSTRY_TYPES, DEFAULT_COLORS } from "../../constants/organization";

const STEPS = [
  { label: "Account Info", icon: PersonIcon },
  { label: "Company Info", icon: BusinessIcon },
  { label: "Contact Details", icon: PhoneIcon },
  { label: "Brand Colors", icon: ColorLensIcon },
  { label: "Review & Submit", icon: CheckCircleIcon },
];

const Signup = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const [formData, setFormData] = useState({
    // Account info
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
    // Company info
    companyName: "",
    industry: "",
    // Contact details
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    // Brand colors
    primaryColor: "#1976D2",
    secondaryColor: "#E3F2FD",
    textColor: "#FFFFFF",
  });

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear errors when user starts typing
    if (error) setError("");
    if (
      passwordError &&
      (field === "password" || field === "confirmPassword")
    ) {
      setPasswordError("");
    }
  };

  const handleColorPresetSelect = (preset) => {
    setFormData((prev) => ({
      ...prev,
      primaryColor: preset.primary,
      secondaryColor: preset.secondary,
      textColor: preset.text,
    }));
  };

  const handleNext = () => {
    // Validate current step before moving forward
    if (!validateStep(activeStep)) {
      return;
    }
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const validateStep = (step) => {
    switch (step) {
      case 0: // Account Info
        if (!formData.email.trim()) {
          setError("Email is required");
          return false;
        }
        if (!isValidEmail(formData.email)) {
          setError("Please enter a valid email address");
          return false;
        }
        if (!formData.firstName.trim()) {
          setError("First name is required");
          return false;
        }
        if (!formData.lastName.trim()) {
          setError("Last name is required");
          return false;
        }
        if (!formData.password) {
          setError("Password is required");
          return false;
        }
        if (formData.password.length < 8) {
          setError("Password must be at least 8 characters");
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          setPasswordError("Passwords do not match");
          return false;
        }
        return true;

      case 1: // Company Info
        if (!formData.companyName.trim()) {
          setError("Company name is required");
          return false;
        }
        if (!formData.industry) {
          setError("Industry type is required");
          return false;
        }
        return true;

      case 2: // Contact Details
        // All optional for now
        return true;

      case 3: // Brand Colors
        // All have defaults
        return true;

      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    setError("");
    setIsLoading(true);

    try {
      // Call signup API
      await authAPI.signup({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        company: {
          name: formData.companyName,
          industry: formData.industry,
          phone: formData.phone || null,
          address: formData.address || null,
          city: formData.city || null,
          state: formData.state || null,
          zipCode: formData.zipCode || null,
          primaryColor: formData.primaryColor,
          secondaryColor: formData.secondaryColor,
          textColor: formData.textColor,
        },
      });

      // Redirect to login on successful signup
      navigate("/login", {
        state: { message: "Account created successfully! Please log in." },
      });
    } catch (err) {
      setError(err.message || "Signup failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const isStepValid = () => {
    switch (activeStep) {
      case 0: // Account Info
        return (
          formData.email.trim().length > 0 &&
          // isValidEmail(formData.email) &&
          formData.firstName.trim().length > 0 &&
          formData.lastName.trim().length > 0 &&
          formData.password.length >= 8 &&
          formData.password === formData.confirmPassword
        );
      case 1: // Company Info
        return (
          formData.companyName.trim().length > 0 && formData.industry.length > 0
        );
      case 2: // Contact Details
        return true; // Optional fields
      case 3: // Brand Colors
        return true; // Colors have defaults
      case 4: // Review
        return true;
      default:
        return true;
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your account with email and password.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  placeholder="John"
                  value={formData.firstName}
                  onChange={handleInputChange("firstName")}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Last Name"
                  placeholder="Doe"
                  value={formData.lastName}
                  onChange={handleInputChange("lastName")}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleInputChange("email")}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  placeholder="Minimum 8 characters"
                  value={formData.password}
                  onChange={handleInputChange("password")}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  helperText="At least 8 characters recommended"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  placeholder="Re-enter password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange("confirmPassword")}
                  required
                  error={!!passwordError}
                  helperText={passwordError}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter your company information.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Company Name"
                  placeholder="e.g., Century 21 - Abrams & Associates"
                  value={formData.companyName}
                  onChange={handleInputChange("companyName")}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Industry Type</InputLabel>
                  <Select
                    value={formData.industry}
                    onChange={handleInputChange("industry")}
                    label="Industry Type"
                    startAdornment={
                      <InputAdornment position="start">
                        <BusinessIcon color="action" />
                      </InputAdornment>
                    }
                  >
                    {INDUSTRY_TYPES.map((industry) => (
                      <MenuItem key={industry} value={industry}>
                        {industry}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add contact information for your company (optional).
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  placeholder="+1 (555) 123-4567"
                  value={formData.phone}
                  onChange={handleInputChange("phone")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Street Address"
                  placeholder="123 Main Street"
                  value={formData.address}
                  onChange={handleInputChange("address")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="City"
                  placeholder="Newark"
                  value={formData.city}
                  onChange={handleInputChange("city")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationCityIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="State"
                  placeholder="NJ"
                  value={formData.state}
                  onChange={handleInputChange("state")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MapIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  placeholder="07102"
                  value={formData.zipCode}
                  onChange={handleInputChange("zipCode")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MarkunreadMailboxIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose brand colors for your company. Select a preset or
              customize.
            </Typography>

            {/* Color Presets */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1, display: "block" }}
            >
              Color Presets
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
              {DEFAULT_COLORS.map((preset) => (
                <Chip
                  key={preset.name}
                  label={preset.name}
                  onClick={() => handleColorPresetSelect(preset)}
                  sx={{
                    backgroundColor: preset.primary,
                    color: preset.text,
                    "&:hover": { backgroundColor: preset.primary, opacity: 0.9 },
                  }}
                />
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Custom Colors */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 2, display: "block" }}
            >
              Custom Colors
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <input
                    type="color"
                    value={formData.primaryColor}
                    onChange={handleInputChange("primaryColor")}
                    style={{
                      width: 40,
                      height: 40,
                      border: "none",
                      cursor: "pointer",
                    }}
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Primary
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formData.primaryColor}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <input
                    type="color"
                    value={formData.secondaryColor}
                    onChange={handleInputChange("secondaryColor")}
                    style={{
                      width: 40,
                      height: 40,
                      border: "none",
                      cursor: "pointer",
                    }}
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Secondary
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formData.secondaryColor}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <input
                    type="color"
                    value={formData.textColor}
                    onChange={handleInputChange("textColor")}
                    style={{
                      width: 40,
                      height: 40,
                      border: "none",
                      cursor: "pointer",
                    }}
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Text
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formData.textColor}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Preview */}
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: "block" }}
              >
                Preview
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 260,
                  height: 36,
                  px: 2,
                  borderRadius: 1.5,
                  border: "2px solid",
                  borderColor: formData.primaryColor,
                  backgroundColor: formData.secondaryColor,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: formData.textColor }}
                >
                  {formData.companyName || "Company Name"}
                </Typography>
              </Box>
            </Box>
          </Box>
        );

      case 4:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please review your information before creating your account.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Account Info */}
              <Box sx={{ p: 2, backgroundColor: "grey.50", borderRadius: 1 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Account Information
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {formData.email}
                </Typography>
                <Typography variant="body2">
                  <strong>Name:</strong> {formData.firstName}{" "}
                  {formData.lastName}
                </Typography>
              </Box>

              {/* Company Info */}
              <Box sx={{ p: 2, backgroundColor: "grey.50", borderRadius: 1 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Company Information
                </Typography>
                <Typography variant="body2">
                  <strong>Company:</strong> {formData.companyName}
                </Typography>
                <Typography variant="body2">
                  <strong>Industry:</strong> {formData.industry}
                </Typography>
                {formData.phone && (
                  <Typography variant="body2">
                    <strong>Phone:</strong> {formData.phone}
                  </Typography>
                )}
                {formData.address && (
                  <Typography variant="body2">
                    <strong>Address:</strong> {formData.address}
                  </Typography>
                )}
              </Box>

              {/* Brand Colors Preview */}
              <Box sx={{ p: 2, backgroundColor: "grey.50", borderRadius: 1 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Brand Colors
                </Typography>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: formData.primaryColor,
                      borderRadius: 1,
                      border: "2px solid",
                      borderColor: "divider",
                    }}
                    title="Primary Color"
                  />
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: formData.secondaryColor,
                      borderRadius: 1,
                      border: "2px solid",
                      borderColor: "divider",
                    }}
                    title="Secondary Color"
                  />
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: formData.textColor,
                      borderRadius: 1,
                      border: "2px solid",
                      borderColor: "divider",
                    }}
                    title="Text Color"
                  />
                  <Typography variant="body2" color="text.secondary">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Preview:
                      </Typography>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: 180,
                          height: 36,
                          px: 2,
                          borderRadius: 1.5,
                          border: "2px solid",
                          borderColor: formData.primaryColor,
                          backgroundColor: formData.secondaryColor,
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ color: formData.textColor }}
                        >
                          {formData.companyName || "Your Brand"}
                        </Typography>
                      </Box>
                    </Box>
                  </Typography>
                </Box>
              </Box>
            </Box >
          </Box >
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: { xs: "auto", sm: "100vh" },
        display: "flex",
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "center",
        backgroundColor: "#f5f5f5",
        py: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 600,
          borderRadius: 2,
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Header Card */}
        <Box
          sx={{
            mb: 3,
            textAlign: "center",
            backgroundColor: theme.palette.primary.main,
            color: "#fff",
            py: { xs: 2, sm: 3 },
            px: { xs: 2, sm: 2 },
            borderRadius: 2,
            marginBottom: 0,
          }}
        >
          <Typography
            variant="h5"
            fontWeight={700}
            color="inherit"
            sx={{ fontSize: { xs: "1.3rem", sm: "1.5rem" } }}
          >
            StatusReport
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              color: "rgba(255, 255, 255, 0.85)",
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            Create your account and organization
          </Typography>
        </Box>

        {/* Stepper Card */}
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: 2,
            p: { xs: 2, sm: 3 },
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Stepper
            activeStep={activeStep}
            orientation={isMobile ? "vertical" : "horizontal"}
            sx={{ mb: { xs: 2, sm: 4 } }}
          >
            {STEPS.map((step) => (
              <Step key={step.label}>
                <StepLabel sx={{ fontSize: { xs: "0.75rem", sm: "inherit" } }}>
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step Content */}
          <Box
            sx={{
              minHeight: { xs: 300, sm: 350 },
              maxHeight: { xs: "55vh", sm: "none" },
              overflowY: { xs: "auto", sm: "visible" },
            }}
          >
            {renderStepContent()}
          </Box>

          {/* Step Actions */}
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1, sm: 2 },
              mt: 4,
              justifyContent: "space-between",
              flexDirection: { xs: "column-reverse", sm: "row" },
            }}
          >
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0}
              fullWidth={{ xs: true, sm: false }}
              sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
            >
              Back
            </Button>

            <Box sx={{ flex: 1, display: { xs: "none", sm: "block" } }} />

            {activeStep === STEPS.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!isStepValid() || isLoading}
                fullWidth={{ xs: true, sm: false }}
                startIcon={
                  isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : undefined
                }
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                {isLoading ? "Creating..." : "Create Account"}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!isStepValid()}
                fullWidth={{ xs: true, sm: false }}
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>

        {/* Login Link */}
        <Box sx={{ mt: 3, textAlign: "center", mb: { xs: 2, sm: 0 } }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
          >
            Already have an account?{" "}
            <Link
              to="/login"
              style={{
                color: "#1976D2",
                textDecoration: "none",
                fontWeight: 600,
              }}
            >
              Sign in
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Signup;
